import { useEffect, useMemo, useState } from 'react'
import { Box, Typography } from '@mui/material'
import { useDispatch, useSelector } from 'react-redux'
import "@fontsource/rubik";
import usePermissions from '../../../hooks/usePermissions'
import { PERMISSION } from '../../../constants/constants'
import { updateReviewState } from '../../../redux/reviewSlice'
import { sortVersionsForDisplay } from '../../../utils/revisionHistory'
import useRevisionHistory from '../hooks/useRevisionHistory'
import VideoPlayer from './VideoPlayer'
import RevisionVersionSelect from './RevisionVersionSelect'
import RevisionNotesPanel from './RevisionNotesPanel'
import RevisionPanelTabs, { REVISION_PANEL_TAB } from '../../../components/revisioncontent/RevisionPanelTabs'
import RevisionContentList from '../../../components/revisioncontent/RevisionContentList'
import RevisionContentUploadBox from '../../../components/revisioncontent/RevisionContentUploadBox'
import useRevisionContents from '../../../hooks/useRevisionContents'

/**
 * The review card: player with a version strip beneath it, notes beside it.
 *
 * One selected version drives all three. Picking a version on the strip moves
 * the player to that cut and the notes panel to that cut's notes, so what is on
 * screen always belongs together.
 *
 * The current cut is selected by default, which leaves the card behaving exactly
 * as it did before versions were pickable: live player, live revisions log.
 *
 * Writing notes stays tied to the current cut. `showLiveLog` requires the
 * current cut to be selected, and selecting a superseded one clears that - so a
 * note can never be recorded with a timestamp taken from a historic video.
 */
const RevisionReviewCard = (props) => {
  const { uploadProps } = props;
  const dispatch = useDispatch()
  const [hasPermission] = usePermissions()
  const commonReducer = useSelector((state) => state.root.commonReducer);
  const reviewEventReducer = useSelector((state) => state.reviewEventReducer);

  const eventId = commonReducer?.production_event_id;
  const currentFinalVideoId = commonReducer?.event_details?.final_video_id;
  // The event's revision is the project's latest one, which after a new cut is
  // delivered still belongs to the previous cut. Only count it for the current
  // cut when it was opened on it - otherwise this cut has no request yet.
  const revisionOnCurrentCut = commonReducer?.event_details?.revision?.revision_video_id === currentFinalVideoId;
  const revisionId = revisionOnCurrentCut ? commonReducer?.event_details?.revision?.id : undefined;
  const revisionSubmitted = revisionOnCurrentCut ? commonReducer?.event_details?.revision?.submitted : undefined;
  const liveNoteCount = reviewEventReducer?.revision_logs?.length;

  // Fetching is driven below rather than on mount, so one effect covers the first
  // load and every later resync.
  const { versions, notesVisible, isLoading, error, fetchHistory } = useRevisionHistory(eventId, false);
  const [selectedVideoId, setSelectedVideoId] = useState(null);
  const [activeTab, setActiveTab] = useState(REVISION_PANEL_TAB.NOTES);

  useEffect(() => {
    // Resync whenever the thing being described changes: a new delivery, a
    // revision opened or submitted, or a note added, edited or deleted in the
    // live log. Links are presigned and expire, so a refetch is never wasted.
    fetchHistory()
  }, [fetchHistory, currentFinalVideoId, revisionId, revisionSubmitted, liveNoteCount])

  const orderedVersions = useMemo(() => sortVersionsForDisplay(versions), [versions]);

  // Derived, not stored: falls back to the current cut, then the newest, so a
  // refetch that drops the open version never leaves an empty panel.
  const selectedVersion =
    orderedVersions.find((version) => version?.final_video_id === selectedVideoId) ||
    orderedVersions.find((version) => version?.final_video_id === currentFinalVideoId) ||
    orderedVersions[0] ||
    null;

  const isCurrentCut = !selectedVersion || selectedVersion?.final_video_id === currentFinalVideoId;

  // Files belong to the revision of whichever cut is selected, so switching
  // versions moves the file list the same way it moves the notes. Falling back
  // to the project's open revision keeps the tab usable on the current cut
  // before the history response has landed.
  const selectedRevisionId = (isCurrentCut ? revisionId : selectedVersion?.revision?.id) || selectedVersion?.revision?.id || revisionId;

  const {
    files: revisionFiles, counts: revisionCounts, submitted: revisionRoundSubmitted, round: revisionRound,
    isLoading: filesLoading, error: filesError, deletingId, deleteFile, refetch: refetchContents,
  } = useRevisionContents({
    eventId,
    revisionId: selectedRevisionId,
    enabled: !!selectedRevisionId,
    // Submitting rewrites `round` and turns `deletable` off on every row without
    // changing any id, so the list has to be re-read rather than left as it was.
    refreshKey: revisionSubmitted,
  });

  // Uploads go to the open request only: the current cut, a revision that has
  // not been submitted, a project still in Review, and a user allowed to add
  // notes (the permission the backend gates revision content on).
  // The server's gate also refuses uploads once the project has used its
  // revisions (403 upload_closed "Need more revision, contact support"), which
  // never applies to an unlimited project.
  const revisionLimitReached =
    commonReducer?.event_details?.unlimited_revision !== true &&
    !(commonReducer?.event_details?.revision_left > 0);

  const canUpload =
    isCurrentCut &&
    !!revisionId &&
    revisionSubmitted !== true &&
    commonReducer?.event_details?.status_type === 'Review' &&
    !revisionLimitReached &&
    hasPermission(PERMISSION.PROJECT.REVISION.NOTES.CREATE);

  const uploadDisabledReason = !isCurrentCut
    ? 'This is an older cut — files can only be added to the open request.'
    : !revisionId
      ? 'Request a revision on this cut to add files for your editor.'
      : revisionSubmitted === true
        ? 'This revision has been submitted. Files can no longer be added.'
        : commonReducer?.event_details?.status_type !== 'Review'
          ? 'This project is not accepting revision files right now.'
          : revisionLimitReached
            ? "You've used all your revisions for this project. Contact Lucihub sales for more revisions."
            : !hasPermission(PERMISSION.PROJECT.REVISION.NOTES.CREATE)
              ? "You don't have permission to add files to this request."
              : '';

  const showFiles = activeTab === REVISION_PANEL_TAB.FILES;

  // The condition that used to decide whether the log panel rendered at all,
  // now also requiring the current cut to be the one on screen.
  const showLiveLog =
    isCurrentCut &&
    reviewEventReducer.request_changes &&
    commonReducer.event_details.status_type === "Review";

  useEffect(() => {
    // Point the player at the selected cut. The current cut needs no override,
    // and a version still transferring has nothing to play - both fall back to
    // the project's own video.
    if (!isCurrentCut && selectedVersion?.video_url) {
      dispatch(updateReviewState({
        history_preview_url: selectedVersion.video_url,
        history_preview_version: selectedVersion.version,
      }))
    } else {
      dispatch(updateReviewState({ history_preview_url: '', history_preview_version: null }))
    }
  }, [dispatch, isCurrentCut, selectedVersion?.video_url, selectedVersion?.version])

  useEffect(() => {
    // Leaving the page must hand the player back to the current cut.
    return () => {
      dispatch(updateReviewState({ history_preview_url: '', history_preview_version: null }))
    }
  }, [dispatch])

  const videoBlocked =
    !hasPermission(PERMISSION.PROJECT.REVISION.FINAL_VIDEO.VIEW) &&
    commonReducer.event_details.status_type_order === 5;

  return (
    <Box
      sx={{
        display: 'flex',
        height: { xs: '67%', sm: '67%', md: '69%', lg: '71%', xl: '71%' },
        width: '100%',
        backgroundColor: '#ffffffbf',
        boxShadow: '0px 5px 15px #00000029',
        borderRadius: '10px',
        alignItems: 'center',
      }}
    >
      <Box sx={{ height: '100%', width: '70%', display: 'flex', flexDirection: 'column' }}>
        {/* Above the player: the picker says which cut you are about to watch
            rather than explaining it afterwards, and stays clear of the player's
            own controls. Nothing to pick between on a single-version project, so
            no picker at all. It still belongs here on the Files tab - it is
            picking which round's files are listed. */}
        {!videoBlocked && (
          <RevisionVersionSelect
            versions={orderedVersions}
            selectedVideoId={selectedVersion?.final_video_id}
            currentFinalVideoId={currentFinalVideoId}
            onSelect={setSelectedVideoId}
          />
        )}

        <Box sx={{ flexGrow: 1, minHeight: 0, display: 'flex', alignItems: 'flex-start', paddingTop: '10px', justifyContent: 'center', overflowY: 'auto' }}>
          {/* The Files tab takes over the player's half of the card: the files
              and their details are the thing being read, and the upload box
              beside them is the thing being used. */}
          {showFiles
            ? (
              <RevisionContentList
                files={revisionFiles}
                counts={revisionCounts}
                isLoading={filesLoading}
                error={filesError}
                deletingId={deletingId}
                onDelete={deleteFile}
                retry={refetchContents}
                submitted={revisionRoundSubmitted}
                round={revisionRound?.round}
              />
            )
            : videoBlocked
              ? <Box height='70%' className='flex col j-center' mt={6}><Typography variant="h3">Video not available for view </Typography></Box>
              : <VideoPlayer />}
        </Box>
      </Box>

      <Box sx={{ height: '100%', mr: 3, width: '30%', maxHeight: '760px', display: 'flex', flexDirection: 'column' }}>
        <RevisionPanelTabs
          value={activeTab}
          onChange={setActiveTab}
          fileCount={revisionCounts?.total || revisionFiles?.length || 0}
        />

        {/* The tab strip brings its own margin, so the panel under it only needs
            the sides and the bottom. */}
        <Box sx={{ flexGrow: 1, minHeight: 0, padding: showFiles ? { xs: '8px 10px 10px', xl: '10px 14px 14px' } : 0 }}>
          {showFiles ? (
            <RevisionContentUploadBox
              uploadProps={uploadProps}
              revisionId={selectedRevisionId}
              eventId={eventId}
              disabled={!canUpload}
              disabledReason={uploadDisabledReason}
            />
          ) : (
            <RevisionNotesPanel
              version={selectedVersion}
              showLiveLog={showLiveLog}
              isCurrentCut={isCurrentCut}
              notesVisible={notesVisible}
              isLoading={isLoading}
              error={error}
              retry={() => fetchHistory()}
              onBackToCurrent={() => setSelectedVideoId(currentFinalVideoId)}
            />
          )}
        </Box>
      </Box>
    </Box>
  )
}

export default RevisionReviewCard
